import { BadgeM } from "@common/components/badge/BadgeM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { ModalM } from "@common/components/modal/ModalM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { IS_WEB } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { subscriptionService } from "@data/services/subscriptionService"
import * as WebBrowser from "expo-web-browser"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { ActivityIndicator, ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect, useSelector } from "react-redux"

function OpenInvoicesModal(props) {
    const { visible, setVisible, userEmail } = props

    const [invoices, setInvoices] = useState([])
    const [loadingInvoiceId, setLoadingInvoiceId] = useState(null)

    // API status hooks
    const getOpenInvoicesApi = useSelector(
        (state) => state.api?.[subscriptionService.getMyOpenInvoices?.name]
    )

    const loadingInvoices = getOpenInvoicesApi?.request

    // Handle load invoices success
    useApiStatus(subscriptionService.getMyOpenInvoices, "", true, (data) => {
        if (data) {
            setInvoices(data)
        }
    })

    // Handle payment URL success
    useApiStatus(
        subscriptionService.getInvoicePaymentUrl,
        "",
        true,
        (data) => {
            setLoadingInvoiceId(null)
            if (data?.hosted_invoice_url) {
                const url = data.hosted_invoice_url
                // Même onglet : un `_blank` ouvert depuis la réponse de l'API
                // sort du geste utilisateur, et les navigateurs mobiles le
                // bloquent sans rien dire — le bouton revient, sans Stripe.
                if (IS_WEB) {
                    window.open(url, "_self")
                } else {
                    WebBrowser.openBrowserAsync(url)
                }
            }
        },
        () => {
            setLoadingInvoiceId(null)
        }
    )

    // Load invoices when modal opens
    useEffect(() => {
        if (visible) {
            loadInvoices()
        } else {
            // Reset state when modal closes
            setInvoices([])
            setLoadingInvoiceId(null)
        }
    }, [visible])

    const loadInvoices = () => {
        props.apiRequest(
            subscriptionService.getMyOpenInvoices,
            {},
            {},
            { email: userEmail }
        )
    }

    const handleClose = () => {
        setInvoices([])
        setLoadingInvoiceId(null)
        setVisible(false)
    }

    const handlePayInvoice = (invoiceId) => {
        setLoadingInvoiceId(invoiceId)
        props.apiRequest(
            subscriptionService.getInvoicePaymentUrl,
            { invoiceId },
            {},
            { email: userEmail }
        )
    }

    return (
        <ModalM
            visible={visible}
            onDismiss={handleClose}
            style={{ maxWidth: 700, width: "90%" }}
        >
            <View style={{ padding: 20 }}>
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: 10,
                    }}
                >
                    <FontAwesome5
                        name="file-invoice-dollar"
                        size={24}
                        color={theme.colors.warning}
                        style={{ marginRight: 12 }}
                    />
                    <TextM fontSize={20} fontWeight="Bold" wrap>
                        Factures à régler
                    </TextM>
                </View>

                {loadingInvoices ? (
                    <View style={{ padding: 40, alignItems: "center" }}>
                        <ActivityIndicator
                            size="large"
                            color={theme.colors.primary}
                        />
                        <TextM style={{ marginTop: 10 }} wrap>
                            Chargement des factures...
                        </TextM>
                    </View>
                ) : invoices.length === 0 ? (
                    <View style={{ padding: 40, alignItems: "center" }}>
                        <FontAwesome5
                            name="check-circle"
                            size={50}
                            color={theme.colors.success}
                            style={{ marginBottom: 15 }}
                        />
                        <TextM
                            fontSize={16}
                            fontWeight="Bold"
                            color={theme.colors.success}
                            wrap
                        >
                            Aucune facture impayée
                        </TextM>
                        <TextM
                            fontSize={14}
                            color={theme.colors.grey}
                            textAlign="center"
                            style={{ marginTop: 10 }}
                            wrap
                        >
                            Tous vos paiements sont à jour !
                        </TextM>
                    </View>
                ) : (
                    <>
                        <TextM
                            fontSize={14}
                            color={theme.colors.grey}
                            style={{ marginBottom: 20 }}
                            wrap
                        >
                            Vous avez {invoices.length} facture(s) en attente de
                            paiement
                        </TextM>

                        {/* Invoice list */}
                        <ScrollView
                            style={{
                                maxHeight: 400,
                                borderWidth: 1,
                                borderColor: theme.colors.light,
                                borderRadius: 8,
                                padding: 10,
                                marginBottom: 20,
                            }}
                        >
                            {invoices.map((invoice) => (
                                <View
                                    key={invoice.id}
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        padding: 12,
                                        backgroundColor:
                                            theme.colors.lightWarning ||
                                            "#FFF9E6",
                                        borderRadius: 8,
                                        marginBottom: 10,
                                        borderLeftWidth: 4,
                                        borderLeftColor: theme.colors.warning,
                                    }}
                                >
                                    <View style={{ flex: 1 }}>
                                        <View
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 8,
                                                marginBottom: 4,
                                            }}
                                        >
                                            <TextM
                                                fontWeight="Bold"
                                                fontSize={16}
                                                wrap
                                            >
                                                €{invoice.amount_due.toFixed(2)}
                                            </TextM>
                                            <BadgeM
                                                customContent="Ouverte"
                                                size={20}
                                                style={{
                                                    backgroundColor:
                                                        theme.colors.warning,
                                                    paddingHorizontal: 8,
                                                    color: theme.colors.white,
                                                }}
                                            />
                                        </View>
                                        <TextM
                                            fontSize={12}
                                            color={theme.colors.grey}
                                            wrap
                                        >
                                            {moment(invoice.created).format(
                                                "DD/MM/YYYY à HH:mm"
                                            )}
                                        </TextM>
                                    </View>

                                    <FilledButtonM
                                        onPress={() =>
                                            handlePayInvoice(invoice.id)
                                        }
                                        label="Régulariser"
                                        loading={
                                            loadingInvoiceId === invoice.id
                                        }
                                        disabled={loadingInvoiceId !== null}
                                        color={theme.colors.success}
                                        style={{ width: 140, marginBottom: 0 }}
                                        labelStyle={{ fontSize: 12 }}
                                        contentStyle={{ height: 38 }}
                                    />
                                </View>
                            ))}
                        </ScrollView>

                        {/* Info message */}
                        <View
                            style={{
                                backgroundColor: theme.colors.lightGrey,
                                borderRadius: 8,
                                padding: 12,
                                marginBottom: 20,
                            }}
                        >
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                }}
                            >
                                <FontAwesome5
                                    name="info-circle"
                                    size={16}
                                    color={theme.colors.primary}
                                    style={{ marginRight: 10, marginTop: 2 }}
                                />
                                <TextM
                                    fontSize={12}
                                    color={theme.colors.grey}
                                    style={{ flex: 1 }}
                                    wrap
                                >
                                    En cliquant sur "Régulariser", vous serez
                                    redirigé vers une page sécurisée Stripe pour
                                    effectuer le paiement.
                                </TextM>
                            </View>
                        </View>
                    </>
                )}

                {/* Close button */}
                <FilledButtonM
                    onPress={handleClose}
                    mode="outlined"
                    label="Fermer"
                    style={{ width: "100%", marginBottom: 0 }}
                    labelStyle={{ fontSize: 14 }}
                    contentStyle={{ height: 45 }}
                />
            </View>
        </ModalM>
    )
}

const mapStateToProps = (state) => {
    return {}
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(OpenInvoicesModal)
